import React, { useState, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import AuthenticationGuard from './components/AuthenticationGuard';
import AuthButton from './components/AuthButton';

/**
 * TMPC NextGen - Main Application
 * Navigation between visualization modules
 */

// Lazy load components for better performance
const ScenarioRunner = lazy(() => import('./components/ScenarioRunner'));
const ApproachVectors = lazy(() => import('./components/ApproachVectors'));
const FlightProfile = lazy(() => import('./components/FlightProfile'));
const MissionPlanner = lazy(() => import('./components/MissionPlanner'));
const MissionObjectivesPlanner = lazy(() => import('./components/MissionObjectivesPlanner'));
const EnhancedMissionObjectivesPlanner = lazy(() => import('./components/EnhancedMissionObjectivesPlanner'));
const TheaterMissionPlanner = lazy(() => import('./components/TheaterMissionPlanner'));
const MissionPlanningEntry = lazy(() => import('./components/MissionPlanningEntry'));
const TargetByTargetMissionPlanner = lazy(() => import('./components/TargetByTargetMissionPlanner'));
const ServiceHealthDashboard = lazy(() => import('./components/ServiceHealthDashboard'));
const TheaterVisualization3D = lazy(() => import('./components/3DTheaterVisualization'));
const StrikeExecution = lazy(() => import('./components/StrikeExecution'));
const Distribution = lazy(() => import('./components/Distribution'));

const COLORS = {
  background: '#070a0d',
  panel: '#0c1117',
  panelBorder: '#1a2533',
  primary: '#00d4ff',
  success: '#00ff88',
  warning: '#ffaa00',
  text: '#e6f0ff',
  textDim: '#5a7090',
};

// Mission Workflow Views - organized by operational sequence
const VIEWS = [
  // Phase 1: Mission Planning
  {
    id: 'ai-planner',
    path: '/',
    name: 'Mission Planning',
    description: 'AI-enhanced objectives-based mission planning with intelligent resource allocation',
    icon: '🎯',
    category: 'primary'
  },

  // Phase 2: Distribution
  {
    id: 'distribution',
    path: '/distribution',
    name: 'Distribution',
    description: 'Mission package distribution and coordination across operational units',
    icon: '📡',
    category: 'primary'
  },

  // Phase 3: Strike Planning
  {
    id: 'theater',
    path: '/strike-planning',
    name: 'Strike Planning',
    description: 'Integrated strike planning with OSINT intelligence, AI planning, and theater simulation',
    icon: '⚔️',
    category: 'primary'
  },

  // Phase 4: Strike Execution
  {
    id: 'execution',
    path: '/execution',
    name: 'Strike Execution',
    description: 'Review and distribute strike order packages to operational units',
    icon: '🚀',
    category: 'primary'
  },

  // Phase 5: Platform Management
  {
    id: 'platform-mgmt',
    path: '/platform-management',
    name: 'Platform Management',
    description: 'Launch platform status, resource management, and system health monitoring',
    icon: '🛡️',
    category: 'primary',
    subItems: ['health']  // System Health will be part of Platform Management
  },

  // Experimental R&D Features (hidden by default, accessible via Dev Tools)
  {
    id: '3d-theater',
    path: '/3d-theater',
    name: '3D Theater (R&D)',
    description: 'Experimental 3D/4D battle theater visualization with terrain, missiles, and threat volumes',
    icon: '🌐',
    category: 'experimental',
    hidden: true
  },

  // Development Tools (hidden by default, accessible via Dev Tools)
  {
    id: 'objectives',
    path: '/mission-objectives',
    name: 'Mission Objectives (Dev)',
    description: 'Original objectives-based planning interface',
    icon: '🎯',
    category: 'development',
    hidden: true
  },
  {
    id: 'scenario',
    path: '/scenario',
    name: 'Scenario Runner (Dev)',
    description: 'Development testing interface for scenario simulation',
    icon: '🎬',
    category: 'development',
    hidden: true
  },
  {
    id: 'vectors',
    path: '/approach-vectors',
    name: 'Approach Vectors (Dev)',
    description: 'Standalone approach vector visualization',
    icon: '🚀',
    category: 'development',
    hidden: true
  },
  {
    id: 'profile',
    path: '/flight-profile',
    name: 'Flight Profile (Dev)',
    description: 'Standalone flight profile analysis',
    icon: '📊',
    category: 'development',
    hidden: true
  },
  {
    id: 'planner',
    path: '/mission-planner',
    name: 'Mission Planner (Dev)',
    description: 'Original mission planning interface',
    icon: '🗺️',
    category: 'development',
    hidden: true
  },
];

// Loading component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: COLORS.background,
    color: COLORS.primary,
    fontFamily: '"Share Tech Mono", monospace',
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      border: `3px solid ${COLORS.panelBorder}`,
      borderTop: `3px solid ${COLORS.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }} />
    <div style={{ marginTop: '20px', letterSpacing: '2px' }}>LOADING MODULE...</div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [missionConfig, setMissionConfig] = useState(null);
  const [showDevTools, setShowDevTools] = useState(false);

  const handleMissionSelect = (config) => {
    console.log('App received mission config:', config);
    setMissionConfig(config);

    if (config.proceedToPlanning) {
      // Navigate to Scenario Runner to generate and run COAs
      navigate('/scenario');
    }
  };

  // Filter views based on whether dev tools are shown
  const visibleViews = VIEWS.filter(view => !view.hidden || showDevTools);

  // Get current view based on URL path
  const currentView = VIEWS.find(view => view.path === location.pathname);


  return (
    <AuthenticationGuard>
    <div style={{
      minHeight: '100vh',
      background: COLORS.background,
      color: COLORS.text,
      fontFamily: '"Rajdhani", sans-serif',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Share+Tech+Mono&display=swap');
        
        * { box-sizing: border-box; margin: 0; padding: 0; }
        
        .nav-btn {
          background: transparent;
          border: 1px solid ${COLORS.panelBorder};
          color: ${COLORS.textDim};
          padding: 10px 16px;
          font-family: 'Share Tech Mono', monospace;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .nav-btn:hover:not(:disabled) {
          border-color: ${COLORS.primary};
          color: ${COLORS.primary};
        }

        .nav-btn.active {
          background: ${COLORS.primary}20;
          border-color: ${COLORS.primary};
          color: ${COLORS.primary};
        }

        .nav-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          color: ${COLORS.textDim};
        }
      `}</style>

      {/* Navigation Header */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: COLORS.panel,
        borderBottom: `1px solid ${COLORS.panelBorder}`,
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: 700,
            letterSpacing: '2px',
            color: COLORS.primary,
          }}>
            TMPC NEXTGEN
          </div>
          <div style={{
            width: '1px',
            height: '24px',
            background: COLORS.panelBorder,
          }} />
          <div style={{ display: 'flex', gap: '4px' }}>
            {visibleViews.map(view => (
              <button
                key={view.id}
                className={`nav-btn ${currentView?.id === view.id ? 'active' : ''}`}
                onClick={() => !view.disabled && navigate(view.path)}
                title={view.description}
                disabled={view.disabled}
              >
                <span>{view.icon}</span>
                <span>{view.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '10px',
          fontFamily: '"Share Tech Mono", monospace',
          color: COLORS.textDim,
        }}>
          <span>v1.0.0</span>
          <a
            href="https://github.com/peraton/tmpc-nextgen"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.textDim, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            📂 Repository
          </a>

          <div style={{
            width: '1px',
            height: '20px',
            background: COLORS.panelBorder,
          }} />

          {/* Dev Tools Toggle */}
          <button
            className="nav-btn"
            onClick={() => setShowDevTools(!showDevTools)}
            title="Toggle developer tools and legacy views"
            style={{
              padding: '6px 10px',
              fontSize: '10px',
              opacity: showDevTools ? 1 : 0.5,
            }}
          >
            <span>{showDevTools ? '🔧' : '⚙️'}</span>
            <span>{showDevTools ? 'Hide' : 'Dev'} Tools</span>
          </button>

          <div style={{
            width: '1px',
            height: '20px',
            background: COLORS.panelBorder,
          }} />

          {/* Authentication - rightmost position */}
          <AuthButton />
        </div>
      </nav>

      {/* Main Content */}
      <main style={{ paddingTop: '52px' }}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<MissionPlanningEntry />} />
            <Route path="/mission-planning/detailed" element={<TargetByTargetMissionPlanner />} />
            <Route path="/distribution" element={<Distribution />} />
            <Route path="/strike-planning" element={<TheaterMissionPlanner />} />
            <Route path="/execution" element={<StrikeExecution />} />
            <Route path="/execution/:planId" element={<StrikeExecution />} />
            <Route path="/platform-management" element={<ServiceHealthDashboard />} />
            <Route path="/3d-theater" element={<TheaterVisualization3D />} />
            <Route path="/mission-objectives" element={<MissionObjectivesPlanner />} />
            <Route path="/scenario" element={<ScenarioRunner />} />
            <Route path="/approach-vectors" element={<ApproachVectors />} />
            <Route path="/flight-profile" element={<FlightProfile />} />
            <Route path="/mission-planner" element={<MissionPlanner />} />
            <Route path="/objectives-legacy" element={<EnhancedMissionObjectivesPlanner />} />
          </Routes>
        </Suspense>
      </main>
    </div>
    </AuthenticationGuard>
  );
}
